/**
 * This file is based on the responsiveDimensions library from
 * https://github.com/react-native-toolkit/react-native-responsive-dimensions
 *
 * MIT License
 *
 * Copyright (c) 2017 react-native-toolkit
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { useState, useEffect, useCallback } from 'react';
import { Dimensions } from 'react-native';
import debounce from 'lodash/debounce';

// declare debounced state functionality
const useDebouncedState = (initialValue, durationInMs = 200) => {
  const [internalState, setInternalState] = useState(initialValue);
  const debouncedFunction = useCallback(
    debounce(setInternalState, durationInMs),
    [setInternalState, durationInMs],
  );
  return [internalState, debouncedFunction];
};

const useDimensionsListener = () => {
  // use debounced state for dimensions
  const [screenDimension, setScreenDimension] = useDebouncedState(
    Dimensions.get('screen'),
  );
  const [windowDimension, setWindowDimension] = useDebouncedState(
    Dimensions.get('window'),
  );

  useEffect(() => {
    function handleDimensionChange({ window, screen }) {
      setWindowDimension(window);
      setScreenDimension(screen);
    }

    const listener = Dimensions.addEventListener(
      'change',
      handleDimensionChange,
    );
    return () => {
      listener.remove();
    };
  }, []);

  return {
    screen: screenDimension,
    window: windowDimension,
  };
};

const percentageCalculation = (max, val) => max * (val / 100);

const fontCalculation = (height, width, val) => {
  const widthDimension = height > width ? width : height;
  const aspectRatioBasedHeight = (16 / 9) * widthDimension;
  return percentageCalculation(
    Math.sqrt(
      Math.pow(aspectRatioBasedHeight, 2) + Math.pow(widthDimension, 2),
    ),
    val,
  );
};

export const responsiveHeight = (h) => {
  const { height } = Dimensions.get('window');
  return percentageCalculation(height, h);
};

export const responsiveWidth = (w) => {
  const { width } = Dimensions.get('window');
  return percentageCalculation(width, w);
};

export const responsiveFontSize = (f) => {
  const { height, width } = Dimensions.get('window');
  return fontCalculation(height, width, f);
};

export const useResponsiveHeight = (h) => {
  const { height } = useDimensionsListener().window;
  return percentageCalculation(height, h);
};

export const useResponsiveWidth = (w) => {
  const { width } = useDimensionsListener().window;
  return percentageCalculation(width, w);
};

export const useResponsiveFontSize = (f) => {
  const { height, width } = useDimensionsListener().window;
  return fontCalculation(height, width, f);
};

export const useResponsiveScreenWidth = (w) => {
  const { width } = useDimensionsListener().screen;
  return percentageCalculation(width, w);
};
